/**
 * BackOfPass — the pass back as an editable row list (Apple renders back
 * fields as label+value rows; links auto-detect on device). Live rows (key
 * conventional for this style's back group — conventionalLiveKeys, NOT the
 * loaded doc) lock their value with the same explainer as the inspector.
 */
import type { PassDesign } from '@insurwiz/wallet-core';

import { useT } from '../../i18n';
import { addField, removeField, setFieldLabelSlugged, setFieldProp } from '../../lib/fieldEditing';

import { conventionalLiveKeyUnion, conventionalLiveKeys } from './liveFieldPalette';

export interface BackOfPassProps {
  design: PassDesign;
  onChange: (next: PassDesign) => void;
  onFlipBack: () => void;
}

export function BackOfPass({ design, onChange, onFlipBack }: BackOfPassProps): JSX.Element {
  const t = useT();
  const rows = design.fields.back ?? [];
  const live = conventionalLiveKeys(design.style).back;
  return (
    <div
      data-testid="back-of-pass"
      className="mx-auto w-[340px] rounded-2xl bg-white ring-1 ring-slate-200 shadow-lg p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onFlipBack}
          className="text-xs text-brand-700 hover:underline"
        >
          {t('back.front')}
        </button>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {t('back.title')}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">
          {/* The live Értesítés (notice) field is boardingPass-only — the empty
              hint must not advertise it on the generic canvas. */}
          {t(design.style === 'boardingPass' ? 'back.empty' : 'back.emptyGeneric')}
        </p>
      ) : null}

      {rows.map((f, i) => {
        const isLive = live.has(f.key);
        // A freshly added custom row has key '' — fall back to a readable
        // accessible name instead of a degenerate ' felirat' / ' érték'.
        const rowName = f.key || t('back.newRowName');
        return (
          <div key={`${f.key}-${i}`} className="rounded-md ring-1 ring-slate-200 p-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <input
                aria-label={t('back.rowLabelAria', { row: rowName })}
                className="input flex-1 text-xs py-1"
                value={f.label ?? ''}
                placeholder={t('back.labelPlaceholder')}
                // Shared slug-on-first-label-edit rule (the panel's): a NEW
                // custom row ({key:''}) gets its key slugged from the label —
                // no key input here, so this is the ONLY way it turns valid.
                // Reserved pool = ALL groups' conventional keys (see
                // FieldInspector — cross-group engine-patch collision).
                onChange={(e) =>
                  onChange(
                    setFieldLabelSlugged(
                      design,
                      'back',
                      i,
                      e.target.value,
                      conventionalLiveKeyUnion(design.style),
                    ),
                  )
                }
              />
              {isLive ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 rounded-full px-1.5 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> {t('back.liveBadge')}
                </span>
              ) : null}
              <button
                type="button"
                aria-label={t('back.rowDeleteAria', { row: rowName })}
                className="text-xs text-red-600 hover:underline"
                onClick={() => onChange(removeField(design, 'back', i))}
              >
                {t('common.delete')}
              </button>
            </div>
            <textarea
              aria-label={t('back.rowValueAria', { row: rowName })}
              rows={2}
              className="input w-full text-xs disabled:bg-slate-100"
              value={f.value}
              disabled={isLive}
              onChange={(e) => onChange(setFieldProp(design, 'back', i, 'value', e.target.value))}
            />
            {isLive ? <p className="text-[10px] text-slate-500">{t('back.liveHint')}</p> : null}
          </div>
        );
      })}

      <button
        type="button"
        className="btn-secondary w-full py-1.5 text-xs"
        onClick={() => onChange(addField(design, 'back'))}
      >
        {t('back.addRow')}
      </button>
    </div>
  );
}
